/**
 * Shared localStorage keys aligned with lawvo-client and doc-center.
 *
 * lawvo-client: `lawvo_state` → { accessToken, user, ... }
 * doc-center:    `dcState`     → { user, jwt }
 */

export interface LawvoPersistedState {
  accessToken?: string;
  user?: {
    id?: number;
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    [key: string]: unknown;
  };
}
export interface DcPersistedState {
  user?: Record<string, unknown>;
  jwt?: string;
}
const LAWVO_STATE_KEY = "lawvo_state";
const LEGACY_STATE_KEY = "state";
const DC_STATE_KEY = "dcState";
const FEATHERS_JWT_KEY = "feathers-jwt";

/** Legacy Translation Workspace keys — migrated into lawvo_state on read */
const LEGACY_ACCESS_TOKEN_KEY = "lawvo_access_token";
const LEGACY_USER_KEY = "lawvo_user";
export function formatBearerToken(token: string): string {
  if (!token) return "";
  return /^Bearer\s/i.test(token) ? token : `Bearer ${token}`;
}
export function loadLawvoState(): LawvoPersistedState | undefined {
  try {
    const raw = localStorage.getItem(LAWVO_STATE_KEY) || localStorage.getItem(LEGACY_STATE_KEY);
    if (raw) return JSON.parse(raw);
    const legacyToken = localStorage.getItem(LEGACY_ACCESS_TOKEN_KEY);
    const legacyUserRaw = localStorage.getItem(LEGACY_USER_KEY);
    if (legacyToken) {
      const user = legacyUserRaw ? JSON.parse(legacyUserRaw) : undefined;
      const migrated: LawvoPersistedState = {
        accessToken: legacyToken,
        user
      };
      saveLawvoState(migrated);
      localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
      localStorage.removeItem(LEGACY_USER_KEY);
      return migrated;
    }
  } catch {
    // ignore
  }
  return undefined;
}
export function saveLawvoState(state: LawvoPersistedState): void {
  try {
    if (!state.accessToken) return;
    localStorage.setItem(LAWVO_STATE_KEY, JSON.stringify(state));
    localStorage.setItem(FEATHERS_JWT_KEY, state.accessToken);
  } catch {
    // ignore
  }
}
export function getLawvoAccessToken(): string {
  return loadLawvoState()?.accessToken || "";
}
export function getLawvoUser(): LawvoPersistedState["user"] | undefined {
  return loadLawvoState()?.user;
}
export function clearLawvoState(): void {
  try {
    localStorage.removeItem(LAWVO_STATE_KEY);
    localStorage.removeItem(LEGACY_STATE_KEY);
    localStorage.removeItem(FEATHERS_JWT_KEY);
    localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
  } catch {
    // ignore
  }
}
export function loadDcState(): DcPersistedState | undefined {
  try {
    const raw = localStorage.getItem(DC_STATE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return undefined;
}
export function saveDcState(state: DcPersistedState): void {
  try {
    if (!state.jwt) return;
    localStorage.setItem(DC_STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}
export function getDcAccessToken(): string {
  return loadDcState()?.jwt || "";
}
export function clearDcState(): void {
  try {
    localStorage.removeItem(DC_STATE_KEY);
  } catch {
    // ignore
  }
}
export function clearAllAuthStorage(): void {
  clearLawvoState();
  clearDcState();
}